import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import { useAuth } from '../lib/auth/AuthContext';
import { getEventState, getMyWeeklyConsent } from '../lib/api/event';
import { issueGameToken } from '../lib/api/game';
import type { GameSession } from '../lib/api/game';
import type { EventPhase } from '../lib/api/event';
import { RegisterLayout } from '../components/register/RegisterLayout';
import { FormError } from '../components/form/FormError';
import { Button } from '../components/Button';
import { useFocusOnMount } from '../lib/hooks/useFocusOnMount';

type GateState =
  | { kind: 'loading' }
  | { kind: 'pre_event'; nextTransitionAt: string | null }
  | { kind: 'post_event' }
  | { kind: 'suspended' }
  | { kind: 'token'; session: GameSession }
  | { kind: 'error'; message: string };

// If the gate can't reach a terminal state within this window, surface an
// error rather than spinning forever. Same defensive pattern as elsewhere
// in the flow — never leave the user staring at an indefinite "Loading…".
const GATE_TIMEOUT_MS = 15_000;

/**
 * /play smart gate. Decides where the user actually goes based on auth +
 * consent state:
 *
 *   anonymous              → /register
 *   PENDING                → /register (resumes at the profile step)
 *   SUSPENDED              → "Account not eligible" notice
 *   ACTIVE, no consent     → /weekly-rules
 *   ACTIVE, has consent    → POST /game/token; standard/DEV users hand off to
 *                            the game, ADMIN pauses behind an explicit "Launch
 *                            Game" click so an admin login isn't trapped in the
 *                            game and can reach the site / admin portal.
 *   phase === PRE_EVENT    → "Coming soon" notice
 *   phase === POST_EVENT   → "Event has ended" notice
 *
 * The real game build isn't deployed yet, so the success branch renders a
 * placeholder. When the game URL lands, swap the placeholder for a redirect
 * (or iframe + postMessage handoff).
 */
export default function PlayPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [gate, setGate] = useState<GateState>({ kind: 'loading' });
  const headingRef = useFocusOnMount<HTMLHeadingElement>();

  useEffect(() => {
    applyPageMeta(PAGE_META.play);
  }, []);

  // Once the gate has minted a session, hand off to the chrome-less /play/game
  // route which mounts the iframe and posts the GameSession. Admins are paused
  // behind an explicit "Launch Game" button below so they aren't trapped in
  // the game on an admin login — the button does its own navigate on click.
  useEffect(() => {
    if (gate.kind !== 'token') return;
    if (auth.user?.type === 'ADMIN') return;
    navigate('/play/game', { replace: true, state: { session: gate.session } });
  }, [gate, auth.user, navigate]);

  // Routing decisions tied directly to auth status — handle these first so
  // we don't make API calls on behalf of an anonymous or PENDING user.
  useEffect(() => {
    if (auth.status === 'loading') return;
    if (!auth.isAuthenticated) {
      navigate('/register', { replace: true });
      return;
    }
    if (auth.user?.status === 'PENDING') {
      navigate('/register', { replace: true });
      return;
    }
    if (auth.user?.status !== 'ACTIVE') {
      // SUSPENDED or any future status we don't recognize — show the
      // 'not eligible' notice rather than risking a stuck loading state.
      setGate({ kind: 'suspended' });
    }
  }, [auth.status, auth.isAuthenticated, auth.user, navigate]);

  // Once we know we have an ACTIVE user, do the phase + consent + token dance.
  useEffect(() => {
    if (auth.status !== 'authenticated' || auth.user?.status !== 'ACTIVE') return;

    const controller = new AbortController();
    // Timeout takes ownership of the gate state directly. If it fires, the
    // in-flight fetch either rejects with AbortError (caught + ignored
    // because `aborted` is already set) or resolves later (its own aborted
    // check short-circuits before updating state). Either way the user
    // sees the error string the timeout sets, not a stuck "Loading…".
    const timeoutId = window.setTimeout(() => {
      controller.abort();
      setGate({
        kind: 'error',
        message: 'This took longer than expected. Please refresh and try again.',
      });
    }, GATE_TIMEOUT_MS);

    (async () => {
      try {
        const state = await getEventState({ signal: controller.signal });
        if (controller.signal.aborted) return;

        if (phaseBlocksPlay(state.phase)) {
          setGate(
            state.phase === 'PRE_EVENT'
              ? { kind: 'pre_event', nextTransitionAt: state.nextTransitionAt }
              : { kind: 'post_event' },
          );
          return;
        }

        const consent = await getMyWeeklyConsent(state.currentWeek, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (!consent.hasConsent) {
          navigate('/weekly-rules', { replace: true });
          return;
        }

        const result = await issueGameToken({ signal: controller.signal });
        if (controller.signal.aborted) return;
        if (result.kind === 'error') {
          if (result.error.code === 'weekly_rules_consent_missing') {
            navigate('/weekly-rules', { replace: true });
            return;
          }
          if (result.error.code === 'user_not_eligible') {
            // Backend suspended/disabled this user between the auth refresh
            // and the token issue — treat as SUSPENDED for UX consistency.
            setGate({ kind: 'suspended' });
            return;
          }
          setGate({
            kind: 'error',
            message: "We couldn't start the game. Please try again in a moment.",
          });
          return;
        }

        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.info('[GH] game session issued — pass this to the game build:', result.session);
        }
        setGate({ kind: 'token', session: result.session });
      } catch {
        if (controller.signal.aborted) return; // timeout already set the gate
        setGate({
          kind: 'error',
          message: 'Something went wrong starting the game. Please try again.',
        });
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [auth.status, auth.user, navigate]);

  // The render branches all share the same a11y pattern: heading is the
  // first focusable element, gets focused on mount via useFocusOnMount.
  // Routes redirects (anonymous, PENDING) render briefly before navigate
  // takes effect — we leave the "Checking your session…" copy for those.

  if (auth.status === 'loading' || gate.kind === 'loading') {
    return (
      <RegisterLayout>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-center font-['Orbitron',sans-serif] text-[clamp(18px,5vw,24px)] uppercase tracking-[0.1em] text-white focus:outline-none"
        >
          {auth.status === 'loading' ? 'Checking your session…' : 'Getting things ready…'}
        </h2>
      </RegisterLayout>
    );
  }

  if (gate.kind === 'suspended') {
    return (
      <RegisterLayout>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-center font-['Orbitron',sans-serif] text-[clamp(18px,5vw,24px)] uppercase tracking-[0.1em] text-white focus:outline-none"
        >
          Account not eligible
        </h2>
        <p className="text-center font-['Arial',sans-serif] text-white/85">
          Your account isn't currently eligible to play. Please contact support if you believe this
          is in error.
        </p>
        <div className="flex justify-center">
          <Button variant="dark" to="/" className="min-w-[200px]">
            Back to home
          </Button>
        </div>
      </RegisterLayout>
    );
  }

  if (gate.kind === 'pre_event') {
    const next = gate.nextTransitionAt ? new Date(gate.nextTransitionAt) : null;
    return (
      <RegisterLayout>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-center font-['Orbitron',sans-serif] text-[clamp(18px,5vw,24px)] uppercase tracking-[0.1em] text-white focus:outline-none"
        >
          See you soon!
        </h2>
        <p className="text-center font-['Arial',sans-serif] text-white/85">
          The Gamerhood event hasn't started yet
          {next && (
            <>
              {' '}— check back{' '}
              <span className="text-[#00ffb2]">
                {next.toLocaleString('en-US', {
                  timeZone: 'America/New_York',
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZoneName: 'short',
                })}
              </span>
            </>
          )}
          .
        </p>
        <div className="flex justify-center">
          <Button variant="dark" to="/" className="min-w-[200px]">
            Back to home
          </Button>
        </div>
      </RegisterLayout>
    );
  }

  if (gate.kind === 'post_event') {
    return (
      <RegisterLayout>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-center font-['Orbitron',sans-serif] text-[clamp(18px,5vw,24px)] uppercase tracking-[0.1em] text-white focus:outline-none"
        >
          That's a wrap!
        </h2>
        <p className="text-center font-['Arial',sans-serif] text-white/85">
          Gamerhood Season 5 has concluded — thanks for playing.
        </p>
        <div className="flex justify-center">
          <Button variant="dark" to="/" className="min-w-[200px]">
            Back to home
          </Button>
        </div>
      </RegisterLayout>
    );
  }

  if (gate.kind === 'error') {
    return (
      <RegisterLayout>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-center font-['Orbitron',sans-serif] text-[clamp(18px,5vw,24px)] uppercase tracking-[0.1em] text-white focus:outline-none"
        >
          Something went wrong
        </h2>
        <FormError>{gate.message}</FormError>
        <div className="flex justify-center">
          <Button variant="dark" to="/" className="min-w-[200px]">
            Back to home
          </Button>
        </div>
      </RegisterLayout>
    );
  }

  const isAdmin = auth.user?.type === 'ADMIN';

  if (isAdmin) {
    return (
      <RegisterLayout>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-center font-['Orbitron',sans-serif] text-[clamp(18px,5vw,24px)] uppercase tracking-[0.1em] text-white focus:outline-none"
        >
          Game session ready
        </h2>
        <p className="text-center font-['Arial',sans-serif] text-white/85">
          Welcome, {auth.user?.displayName || auth.user?.firstName || 'admin'}. You're signed in as
          an admin, so we paused here instead of jumping straight into the game.
        </p>
        <div className="flex flex-col items-center gap-[16px]">
          <Button
            variant="primary"
            onClick={() =>
              navigate('/play/game', { state: { session: gate.session } })
            }
            className="min-w-[200px]"
          >
            Launch Game
          </Button>
          <Button variant="dark" to="/admin" className="min-w-[200px]">
            Admin Portal
          </Button>
        </div>
      </RegisterLayout>
    );
  }

  // Non-admin success path navigates via the effect above. Briefly renders
  // nothing while React Router transitions to /play/game.
  return null;
}

function phaseBlocksPlay(phase: EventPhase): boolean {
  return phase === 'PRE_EVENT' || phase === 'POST_EVENT';
}
